import { Component } from '@builder.io/sdk';

import imageField from 'molecules/Image/config';
import sectionFields, { SectionConfigProps } from 'molecules/Section/config';

import { idField } from 'utils/builder/fields';

interface ExtensionColumnImage {
  file?: { url?: string };
  alt?: string;
}

interface ExtensionBullet {
  text?: string;
}

interface ExtensionColumn {
  id?: string;
  image?: ExtensionColumnImage;
  badge?: string;
  heading?: string;
  description?: string;
  howLabel?: string;
  bullets?: ExtensionBullet[];
}

const productExtensionColumnsFields = [
  {
    name: 'eyebrow',
    type: 'string',
    defaultValue: 'How Prismatic Extends Your Product',
    helperText: 'Small overline shown above the heading.',
  },
  {
    name: 'heading',
    type: 'string',
    defaultValue: 'Take your B2B SaaS to the next level',
    helperText: 'Main section heading.',
  },
  {
    name: 'columns',
    type: 'list',
    helperText:
      'Each column: a product screenshot, a step badge, a heading, a description, and a "HOW" box with bullets. Designed for 3 columns; the HOW boxes are kept the same height and top-aligned automatically.',
    defaultValue: [
      {
        id: 'connect',
        image: {
          file: { url: '/images/product-extension/connect.png' },
          alt: 'Acme SaaS marketplace built with Prismatic',
        },
        badge: 'Connect',
        heading: 'Connect everything. Skip the infra.',
        description: 'Create integrations faster with AI, Prismatic Skills, SDKs, and your IDE.',
        howLabel: 'HOW',
        bullets: [
          { text: 'Write TypeScript in your IDE with AI assistance' },
          { text: 'Take full control of the platform via CLI and API' },
        ],
      },
      {
        id: 'automate',
        image: { file: { url: '/images/product-extension/automate.png' }, alt: 'Acme SaaS low-code workflow builder' },
        badge: 'Automate',
        heading: 'Enable customers to build workflows',
        description: 'Customers handle one-off requests themselves, without using your engineers.',
        howLabel: 'HOW',
        bullets: [
          { text: 'Create workflows with templates on a low-code canvas' },
          { text: 'Use an AI Copilot for natural-language development' },
        ],
      },
      {
        id: 'act',
        image: { file: { url: '/images/product-extension/act.png' }, alt: 'Acme SaaS AI chat assistant' },
        badge: 'Act',
        heading: 'Customers need AI. You are ready.',
        description: 'Turn any flow in an integration or workflow into an AI-accessible tool.',
        howLabel: 'HOW',
        bullets: [
          { text: 'Define any flow as a tool.' },
          { text: 'Make that tool accessible via the MCP flow server.' },
        ],
      },
    ],
    subFields: [
      idField,
      imageField('image'),
      {
        name: 'badge',
        type: 'string',
        defaultValue: 'Connect',
        helperText: 'Step label shown in the teal pill (rendered uppercase).',
      },
      { name: 'heading', type: 'string', required: true },
      { name: 'description', type: 'string' },
      { name: 'howLabel', type: 'string', defaultValue: 'HOW' },
      {
        name: 'bullets',
        type: 'list',
        helperText: 'Bullet points shown inside the HOW box.',
        subFields: [{ name: 'text', type: 'string', required: true }],
      },
    ],
  },
  ...sectionFields,
] as const;

const productExtensionColumnsConfig = {
  name: 'ProductExtensionColumns',
  inputs: productExtensionColumnsFields,
} as Component;

export interface ProductExtensionColumnsConfigProps extends SectionConfigProps {
  eyebrow?: string;
  heading?: string;
  columns?: ExtensionColumn[];
}

export default productExtensionColumnsConfig;
